import { useCallback, useEffect, useMemo, useRef } from 'react';
import posthog from 'posthog-js';
import { EVENTS } from '../constants/events';
import { useAnalytics } from './useAnalytics';

/**
 * Analytics for the web2app funnel (`/funnel-auth`).
 *
 * The whole funnel is one route with steps held in React state, so PostHog's
 * automatic pageview capture can only ever see a single page for the entire
 * flow. Every step therefore reports itself twice, on purpose:
 *
 *  1. `funnel_step_viewed` (+ `step`/`step_number`) — the event to build the
 *     PostHog funnel insight on. `step_number` makes the drop-off chart order
 *     itself without anyone hand-sorting step names in the UI.
 *  2. a synthetic `$pageview` on a virtual path (`/funnel-auth/<step>`) — so
 *     the step also shows up as its own page in Web Analytics / Paths, which
 *     is where funnel steps are usually looked for first.
 *
 * The virtual pageview deliberately carries no query string: the funnel URL
 * holds `redirect_uri`/`state` and, on the way back from Google, the OAuth
 * `code` — none of which belong in analytics.
 */

const FUNNEL_NAME = 'web2app';

// Ordered funnel views, including the transient ones ('generating',
// 'finishing') that no one clicks but everyone passes through, and the
// "See how Pixley works" slides (FunnelExplainer's EXPLAINER_STEPS) that only
// visitors who ask to see them pass through. Index + 1 is the reported
// `step_number`.
export const FUNNEL_VIEW_ORDER = [
  'welcome',
  'friend',
  'episode',
  'personal',
  'name',
  'create',
  'generating',
  'meet',
  'signin',
  'finishing',
];

// Fires `funnel_started` once per funnel session rather than once per page
// load — the Google round trip reloads the page mid-flow, and a reload isn't
// a new start.
export const FUNNEL_STARTED_KEY = 'rc_funnel_started';

const DEFAULT_PATH_PREFIX = '/funnel-auth';

/**
 * @param {object} context Funnel answers so far. Merged into every event, so
 *   drop-off can be sliced by them without a join. Read
 *   through a ref, so handlers always send the latest values.
 * @param {object} [options] Per-funnel identity. There is more than one web2app
 *   funnel (the full onboarding at /funnel-auth, the short intro flow at
 *   /funnel-intro), and they need to be separable in PostHog — otherwise their
 *   steps land in one insight with clashing `step_number`s. Defaults reproduce
 *   the original /funnel-auth behaviour exactly.
 * @param {string} [options.funnelName] Value of the `funnel` property on every event.
 * @param {string[]} [options.viewOrder] Ordered views, for `step_number`/`total_steps`.
 * @param {string} [options.pathPrefix] Base path of the synthetic `$pageview`s.
 * @param {string} [options.startedKey] sessionStorage key guarding `funnel_started`.
 *   Distinct per funnel, so abandoning one and entering the other in the same
 *   tab still registers as a start.
 */
export const useFunnelAnalytics = (context = {}, options = {}) => {
  const { trackEvent } = useAnalytics();

  // Frozen on first render: the config is static per page, and holding it in a
  // ref keeps it out of the callbacks' dependency arrays. Callers pass an
  // object literal, so depending on it directly would rebuild every handler on
  // every render — and `trackView` runs from an effect keyed on this hook's
  // return value.
  const configRef = useRef(null);
  if (configRef.current === null) {
    configRef.current = {
      funnelName: options.funnelName || FUNNEL_NAME,
      viewOrder: options.viewOrder || FUNNEL_VIEW_ORDER,
      pathPrefix: options.pathPrefix || DEFAULT_PATH_PREFIX,
      startedKey: options.startedKey || FUNNEL_STARTED_KEY,
    };
  }

  const contextRef = useRef(context);
  useEffect(() => {
    contextRef.current = context;
  });

  const lastViewRef = useRef(null);
  const invalidLinkFiredRef = useRef(false);

  const track = useCallback(
    (event, properties = {}) => {
      trackEvent(event, {
        funnel: configRef.current.funnelName,
        ...contextRef.current,
        ...properties,
      });
    },
    [trackEvent]
  );

  // For events fired immediately before leaving the page (the redirect back to
  // the RevenueCat funnel, the hop out to Google). PostHog normally batches
  // captures, and a navigation can outrun the next flush — `send_instantly`
  // skips the queue so the conversion isn't the one event that goes missing.
  const trackImmediate = useCallback(
    (event, properties = {}) => {
      try {
        posthog.capture(
          event,
          {
            funnel: configRef.current.funnelName,
            ...contextRef.current,
            ...properties,
            // Matches what useAnalytics.trackEvent attaches to every event
            timestamp: new Date().toISOString(),
            source: 'pixley_web_app',
          },
          { send_instantly: true }
        );
      } catch (_) {
        // Analytics must never break the funnel.
      }
    },
    []
  );

  const stepProps = useCallback(
    (view, properties = {}) => ({
      step: view,
      step_number: configRef.current.viewOrder.indexOf(view) + 1,
      total_steps: configRef.current.viewOrder.length,
      ...properties,
    }),
    []
  );

  const trackView = useCallback(
    (view) => {
      // Guards double-firing from React StrictMode's dev double-effects and
      // from re-renders that don't change the view.
      if (!view || lastViewRef.current === view) return;
      lastViewRef.current = view;

      if (!sessionStorage.getItem(configRef.current.startedKey)) {
        sessionStorage.setItem(configRef.current.startedKey, '1');
        track(EVENTS.FUNNEL_STARTED, {
          entry_step: view,
          // A visitor entering anywhere but the welcome screen resumed a
          // funnel they'd already started (reload, or back from Google).
          is_resumed: view !== 'welcome',
        });
      }

      track(EVENTS.FUNNEL_STEP_VIEWED, stepProps(view));

      try {
        const path = `${configRef.current.pathPrefix}/${view}`;
        posthog.capture('$pageview', {
          $current_url: `${window.location.origin}${path}`,
          $pathname: path,
          funnel: configRef.current.funnelName,
          ...stepProps(view),
        });
      } catch (_) {
        // Analytics must never break the funnel.
      }
    },
    [track, stepProps]
  );

  // `immediate` for the sign-in step, whose completion is followed straight
  // away by the redirect out to Google.
  const trackStepCompleted = useCallback(
    (view, properties, { immediate = false } = {}) =>
      (immediate ? trackImmediate : track)(
        EVENTS.FUNNEL_STEP_COMPLETED,
        stepProps(view, properties)
      ),
    [track, trackImmediate, stepProps]
  );

  const trackInvalidLink = useCallback(() => {
    if (invalidLinkFiredRef.current) return;
    invalidLinkFiredRef.current = true;
    track(EVENTS.FUNNEL_INVALID_LINK, {
      // Which half is missing tells a broken RevenueCat funnel config apart
      // from someone finding the URL on their own.
      has_redirect_uri: !!new URLSearchParams(window.location.search).get('redirect_uri'),
    });
  }, [track]);

  const setPersonProperties = useCallback((properties) => {
    try {
      posthog.setPersonProperties(properties);
    } catch (_) {
      // ignore
    }
  }, []);

  return useMemo(
    () => ({
      track,
      trackImmediate,
      trackView,
      trackStepCompleted,
      trackInvalidLink,
      setPersonProperties,
    }),
    [track, trackImmediate, trackView, trackStepCompleted, trackInvalidLink, setPersonProperties]
  );
};

export default useFunnelAnalytics;
